import { Done, ForwardToInbox, Mail, Save, Warning } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    type SelectChangeEvent,
    Typography,
} from '@mui/material';
import { EMailCycle } from '@sharedInterfaces/globalEnums';
import { EMicrosoftConnectionState, type ICompanyEmailSettings } from '@sharedInterfaces/ICompany';
import { EEmailTransferOption } from '@sharedInterfaces/ICompanySettings';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import companyEmailSettingsSendMail from '@src/APIs/graphQl/Company/companyEmailSettingsSendMail';
import companyEmailSettingsSendMailExchange from '@src/APIs/graphQl/Company/companyEmailSettingsSendMailExchange';
import editCompanyEmailSettings from '@src/APIs/graphQl/Company/editCompanyEmailSettings';
import editEmailTransferOption from '@src/APIs/graphQl/Company/editEmailTransferOption';
import loadEmailMailboxes from '@src/APIs/graphQl/Company/loadEmailMailboxes';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import type Company from '@src/Objects/Company';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

interface EmailSettingsPageProps {
    emailSettings: ICompanyEmailSettings;
    globalEmailSupport: boolean;
    microsoftConnectionStatus: EMicrosoftConnectionState;
    setCompany: (company: Company) => void;
}
export default function EmailSettingsPage({
    emailSettings,
    globalEmailSupport,
    microsoftConnectionStatus,
    setCompany,
}: EmailSettingsPageProps) {
    const showErrorDialog = useErrorDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;

    const [emailOption, setEmailOption] = useState<EEmailTransferOption>(emailSettings.emailTransferOption);
    const [saving, setSaving] = useState<boolean>(false);
    const [loadingMailboxes, setLoadingMailboxes] = useState<boolean>(false);
    const [mailboxes, setMailboxes] = useState<string[] | null>(null);
    const [mailbox, setMailbox] = useState<string>(emailSettings.exchangeMailbox);
    const [savingMailbox, setSavingMailbox] = useState<boolean>(false);
    const [testingSuccessfull, setTestingSuccessfull] = useState<boolean | null>(null);
    const [savingSuccess, setSavingSuccess] = useState<boolean | null>(null);

    const canSend =
        emailSettings.emailTransferOption !== EEmailTransferOption.NoEmail &&
        !(emailSettings.emailTransferOption === EEmailTransferOption.Global && !globalEmailSupport) &&
        !saving;

    useEffect(() => {
        if (emailOption === EEmailTransferOption.Exchange) {
            loadMailboxes();
        }
    }, []);

    const handleEmailOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as EEmailTransferOption;
        //TODO: Filter ob Exchange, dann fragen nach Konto...

        setEmailOption(value);
        if (value === emailSettings.emailTransferOption) return;
        if (value === EEmailTransferOption.Exchange) {
            return loadMailboxes();
        }
        saveEmailTransferOption(value);
    };
    const forecastMail = <ForecastSettings disabled={!canSend} emailSettings={emailSettings} setCompany={setCompany} />;
    const certificateMail = (
        <CertificateMailSettings disabled={!canSend} emailSettings={emailSettings} setCompany={setCompany} />
    );

    function onChangeMailbox(event: SelectChangeEvent<string>) {
        const mailbox = event.target.value as string;
        setMailbox(mailbox);
        setTestingSuccessfull(null);
    }

    function saveEmailTransferOption(value: EEmailTransferOption) {
        setSaving(true);
        editEmailTransferOption(value)
            .then(setCompany)
            .catch((err) => {
                showErrorDialog(err);
                setEmailOption(emailSettings.emailTransferOption);
            })
            .finally(() => setSaving(false));
    }

    function loadMailboxes() {
        setLoadingMailboxes(true);
        loadEmailMailboxes()
            .then((mailboxes) => {
                // console.log(mailboxes);
                setMailboxes(mailboxes);
            })
            .catch((err) => {
                showErrorDialog(err);
                setMailboxes([]);
            })
            .finally(() => setLoadingMailboxes(false));
    }

    return (
        <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {langStrings.emailSettings}
                </Typography>
                <Divider />

                <FormControl component="fieldset">
                    <FormLabel component="legend">{langStrings.chooseEmailSendMethod}</FormLabel>
                    <Box sx={{ mt: 2 }}>
                        <FormControl>
                            <FormLabel>{langStrings.emailSendMethod}</FormLabel>
                            <RadioGroup value={emailOption} onChange={handleEmailOptionChange}>
                                <FormControlLabel
                                    value="noEmail"
                                    control={<Radio />}
                                    label={langStrings.noEmail}
                                    disabled={saving}
                                />
                                <FormControlLabel
                                    value="global"
                                    control={<Radio />}
                                    label={langStrings.sendThroughGlobal}
                                    disabled={!globalEmailSupport || saving}
                                />
                                <FormControlLabel
                                    value="exchange"
                                    control={<Radio />}
                                    label={langStrings.sendThroughExchange}
                                    disabled={
                                        microsoftConnectionStatus !== EMicrosoftConnectionState.Connected || saving
                                    }
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                </FormControl>
                {emailOption === EEmailTransferOption.Exchange && (
                    <FormControl>
                        <FormLabel>{langStrings.emailExchangeMailbox}</FormLabel>
                        <Select
                            value={mailbox}
                            disabled={saving || loadingMailboxes}
                            onChange={onChangeMailbox}
                            size="small"
                        >
                            <MenuItem key={'-'} value={'-'}>
                                - Nicht Ausgewählt -
                            </MenuItem>
                            {mailboxes &&
                                mailboxes.map((mailbox) => (
                                    <MenuItem key={mailbox} value={mailbox}>
                                        {mailbox}
                                    </MenuItem>
                                ))}
                        </Select>
                        {loadingMailboxes && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2">Loading...</Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<Mail />}
                                disabled={
                                    saving || loadingMailboxes || savingMailbox || mailbox === '-' || mailbox === ''
                                }
                                onClick={() => {
                                    setTestingSuccessfull(null);
                                    setSavingMailbox(true);
                                    companyEmailSettingsSendMailExchange(mailbox)
                                        .then(() => setTestingSuccessfull(true))
                                        .catch((err) => {
                                            showErrorDialog(err);
                                            setTestingSuccessfull(false);
                                        })
                                        .finally(() => {
                                            setSavingMailbox(false);
                                        });
                                }}
                                sx={{ maxWidth: 250 }}
                            >
                                {langStrings.sendToMe}
                            </Button>
                            {testingSuccessfull !== null && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {testingSuccessfull ? (
                                        <>
                                            <Typography color="success.main">
                                                {langStrings.sendingSuccessfull}
                                            </Typography>
                                            <Done color="success" />
                                        </>
                                    ) : (
                                        <>
                                            <Typography color="error.main">
                                                {langStrings.sendingNotSuccessfull}
                                            </Typography>
                                            <Warning color="error" />
                                        </>
                                    )}
                                </Box>
                            )}
                            {emailSettings.exchangeMailbox !== mailbox && (
                                <Button
                                    variant="contained"
                                    startIcon={<Save />}
                                    disabled={
                                        saving ||
                                        loadingMailboxes ||
                                        savingMailbox ||
                                        mailbox === '-' ||
                                        mailbox === '' ||
                                        !testingSuccessfull
                                    }
                                    onClick={() => {
                                        setSavingMailbox(true);
                                        setSavingSuccess(null);
                                        editEmailTransferOption(emailOption, mailbox)
                                            .then((c) => {
                                                setCompany(c);
                                                setSavingSuccess(true);
                                            })
                                            .catch((err) => {
                                                showErrorDialog(err);
                                                setSavingSuccess(false);
                                            })
                                            .finally(() => {
                                                setSavingMailbox(false);
                                            });
                                    }}
                                    sx={{ maxWidth: 250 }}
                                >
                                    {langStrings.save}
                                </Button>
                            )}
                            {savingSuccess !== null && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {savingSuccess ? (
                                        <>
                                            <Typography color="success.main">
                                                {langStrings.savingSuccessfull}
                                            </Typography>
                                            <Done color="success" />
                                        </>
                                    ) : (
                                        <>
                                            <Typography color="error.main">
                                                {langStrings.savingNotSuccessfull}
                                            </Typography>
                                            <Warning color="error" />
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </FormControl>
                )}
                <Divider sx={{ my: 3 }} />
                {forecastMail}
                {certificateMail}
            </Box>
        </Paper>
    );
}

interface CertificateMailSettingsProps {
    emailSettings: ICompanyEmailSettings;
    disabled: boolean;
    setCompany: (company: Company) => void;
}
function CertificateMailSettings({ emailSettings, disabled, setCompany }: CertificateMailSettingsProps) {
    const showErrorDialog = useErrorDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    const [saving, setSaving] = useState<boolean>(false);
    const [checked, setChecked] = useState<boolean>(emailSettings.sendCertificateReminder ? true : false);

    useEffect(() => {
        setChecked(emailSettings.sendCertificateReminder ? true : false);
    }, [emailSettings, emailSettings.sendCertificateReminder]);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5">{langStrings.certificateReminder}</Typography>
            <FormControl>
                <FormLabel>{langStrings.activateCertificateReminder}</FormLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox disabled={saving || disabled} checked={checked} onChange={saveChanges} />
                    {saving && <CircularProgress size={16} />}
                </Box>
            </FormControl>
        </Box>
    );

    function saveChanges(e: React.ChangeEvent<HTMLInputElement>) {
        setSaving(true);
        editCompanyEmailSettings(
            emailSettings.forecastCycle,
            emailSettings.forecastText,
            emailSettings.sendForecastMail,
            emailSettings.sendUpdateReminderMail,
            e.target.checked,
        )
            .then(setCompany)
            .catch((err) => {
                showErrorDialog(err);
                setChecked(emailSettings.sendCertificateReminder ? true : false);
            })
            .finally(() => setSaving(false));
    }
}

interface ForecastSettingsProps {
    emailSettings: ICompanyEmailSettings;
    disabled: boolean;
    setCompany: (company: Company) => void;
}
function ForecastSettings({ emailSettings, disabled, setCompany }: ForecastSettingsProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;

    const [cycle, setCycle] = useState<EMailCycle>(emailSettings.forecastCycle);
    const [forecastText, setForecastText] = useState(emailSettings.forecastText);
    const [sendForecastMail, setSendForecastMail] = useState(emailSettings.sendForecastMail);
    const [sendUpdateReminderMail, setSendUpdateReminderMail] = useState(emailSettings.sendUpdateReminderMail);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [changed, setChanged] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);

    const sended = () => {};
    const handleChange = (event: SelectChangeEvent<EMailCycle>) => {
        setCycle(event.target.value as EMailCycle);
        setChanged(true);
    };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h5">{langStrings.reminderMail}</Typography>

            <FormControl>
                <FormLabel>{langStrings.mailSendHowOften}</FormLabel>
                <Select value={cycle} disabled={disabled || saving} onChange={handleChange} size="small">
                    <MenuItem value={EMailCycle.NEVER}>{langStrings.oftenUsed.never}</MenuItem>
                    <MenuItem value={EMailCycle.MONTLY}>{langStrings.oftenUsed.monthly}</MenuItem>
                </Select>
            </FormControl>

            <FormControl>
                <FormLabel>{langStrings.sendForecastMail}</FormLabel>
                <Checkbox
                    disabled={disabled || saving}
                    checked={sendForecastMail}
                    onChange={(e) => {
                        setSendForecastMail(e.target.checked);
                        setChanged(true);
                    }}
                />
            </FormControl>

            <FormControl>
                <FormLabel>{langStrings.sendUpdateReminderMail}</FormLabel>
                <Checkbox
                    disabled={disabled || saving}
                    checked={sendUpdateReminderMail}
                    onChange={(e) => {
                        setSendUpdateReminderMail(e.target.checked);
                        setChanged(true);
                    }}
                />
            </FormControl>

            <FormControl>
                <FormLabel>{langStrings.mailForecastText}</FormLabel>
                <FormatedTextInput
                    value={forecastText}
                    disabled={disabled || saving}
                    onChange={(text) => {
                        setForecastText(text);
                        setChanged(true);
                    }}
                />
            </FormControl>

            {saveError && (
                <Alert severity="error" onClose={() => setSaveError(null)}>
                    {saveError}
                </Alert>
            )}

            {sending && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body1">{langStrings.oftenUsed.sending}</Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 250 }}>
                {changed && (
                    <Button
                        variant="contained"
                        startIcon={<Done />}
                        disabled={saving || disabled}
                        onClick={saveChanges}
                    >
                        {langStrings.save}
                    </Button>
                )}

                <Button variant="outlined" startIcon={<Mail />} disabled={saving || disabled} onClick={sendTestMail}>
                    {langStrings.sendToMe}
                </Button>

                <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<ForwardToInbox />}
                    disabled={saving || changed || disabled}
                    onClick={sendNow}
                >
                    {langStrings.sendNow}
                </Button>

                <Typography variant="subtitle2" color="text.secondary">
                    {langStrings.sendNowSubtitle}
                </Typography>
            </Box>
        </Box>
    );

    function saveChanges() {
        setSaving(true);
        editCompanyEmailSettings(
            cycle,
            forecastText,
            sendForecastMail,
            sendUpdateReminderMail,
            emailSettings.sendCertificateReminder,
        )
            .then(setCompany)
            .then(setChanged.bind(null, false))
            .catch((err) => setSaveError(err.toString()))
            .finally(() => setSaving(false));
    }

    function sendTestMail() {
        setSaving(true);
        setSending(true);
        companyEmailSettingsSendMail(true, forecastText, sendForecastMail, sendUpdateReminderMail)
            .then(sended)
            .catch((err) => setSaveError(err.toString()))
            .finally(() => {
                setSaving(false);
                setSending(false);
            });
    }

    function sendNow() {
        const confirmed = confirm(langStrings.areYouSureForecastMail);
        if (!confirmed) return;
        setSaving(true);
        setSending(true);
        companyEmailSettingsSendMail(false) //Ohne weitere Parameter,die gespeicherten werden verwendet+
            .then(sended)
            .catch((err) => setSaveError(err.toString()))
            .finally(() => {
                setSaving(false);
                setSending(false);
            });
    }
}
